"use client";

import { useQuery } from "@apollo/client/react";
import { toPlainDateString } from "@sa-nu/shared-utils/date";

import { graphql, useFragment } from "../../../../__generated__/gql";
import {
  AccommodationOnRoomDetailContent,
  accommodationToRoomDetailView,
} from "../_utils/to-room-detail-view";
import type { CtaAction } from "../content/cta-button";
import type { AccommodationRoomDetailPayload } from "../room-detail-drawer-provider";

import { BodyFrame } from "./frame";

/**
 * RoomDetailDrawer (accommodation 経路) 表示用クエリ。
 *
 * - `accommodation(id)` で部屋情報
 * - `availability` が指定された場合は `availabilities(firstNight, lastNight, provider)`
 *   で 1 泊分の availability も同時に取得し CTA の日付/価格に使う
 * - `availability` 無し時は `$skipAvailability=true` で availabilities フィールドをスキップ
 *
 * Storybook 等で mock するために export する。
 */
export const RoomDetailDrawerQuery = graphql(`
  query RoomDetailDrawer(
    $id: ID!
    $firstNight: PlainDate
    $provider: AccommodationReservationProviderInput
    $skipAvailability: Boolean!
  ) {
    accommodation(id: $id) {
      id
      ...AccommodationOnRoomDetailContent
      availabilities(
        firstNight: $firstNight
        lastNight: $firstNight
        provider: $provider
      ) @skip(if: $skipAvailability) {
        ...AvailabilityOnCtaButton
      }
    }
  }
`);

/**
 * accommodation 経路の body。`accommodationId` と (任意で) `availability` から
 * 部屋情報 + availability を fetch し、`availability` の有無で CtaAction を
 * `reserve | close` に振り分ける。
 */
export function AccommodationBody({
  payload,
  onCloseDrawer,
  onOpenNestedDrawer,
  onCtaMount,
}: {
  payload: AccommodationRoomDetailPayload;
  onCloseDrawer: () => void;
  onOpenNestedDrawer: () => void;
  onCtaMount: (el: HTMLButtonElement | null) => void;
}) {
  const skipAvailability = !payload.availability;
  const { data, loading, error } = useQuery(RoomDetailDrawerQuery, {
    variables: {
      id: payload.accommodationId,
      firstNight: payload.availability
        ? toPlainDateString(payload.availability.firstNight)
        : null,
      provider: payload.availability?.provider ?? null,
      skipAvailability,
    },
  });
  // useFragment は早期 return より前で無条件に呼ぶ（rules-of-hooks）。
  const accommodation = useFragment(
    AccommodationOnRoomDetailContent,
    data?.accommodation,
  );

  const view = accommodation
    ? accommodationToRoomDetailView(accommodation)
    : null;

  // CTA は availability の有無で 2 パターン。reserve の availabilityFragmentData は
  // `availabilities` 配列の先頭 (firstNight == lastNight なので 1 件想定だが、
  // 万一空配列だった場合は close CTA にフォールバックして runtime crash を避ける)。
  // reserve 押下でネスト Drawer (チェックアウト日選択) を開く。
  const firstAvailability = data?.accommodation?.availabilities?.[0];
  const primaryAction: CtaAction =
    payload.availability && firstAvailability
      ? {
          kind: "reserve",
          availabilityFragmentData: firstAvailability,
          onAction: onOpenNestedDrawer,
          buttonRef: onCtaMount,
        }
      : {
          kind: "close",
          onAction: onCloseDrawer,
        };

  const loadingAction: CtaAction | undefined = payload.availability
    ? { kind: "reserve", loading: true, onAction: () => {} }
    : undefined;

  return (
    <BodyFrame
      loading={loading}
      error={!!error || !data?.accommodation || !accommodation}
      view={view}
      primaryAction={primaryAction}
      loadingAction={loadingAction}
    />
  );
}
